import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Button } from '../atoms';
import { DataTable, StatCard, useToast, ManageParentMembersModal } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * ParentClientRollup — /v2/parent-clients/:id
 *
 * Domain 17.2 parent-company roll-up view. Reads the FROZEN
 * GET /api/parent-client-rollup/:parentId contract (src/routes/crm.js):
 *   { parent, subsidiaries:[{ client, property_count, parcel_count,
 *     ytd_savings?, open_invoice_total? }],
 *     totals:{ subsidiary_count, property_count, parcel_count,
 *     ytd_savings?, open_invoice_total? } }
 *
 * Money aggregates (ytd_savings / open_invoice_total) are OMITTED by the
 * backend for money-blind roles (the office administrator) — the UI renders
 * only the fields present, so the money-blind view shows counts alone. Each
 * subsidiary row links through to its own ClientDetail.
 *
 * Backlog item 17.2 (Entity-contacts + parent-clients rollup FE).
 */

interface ParentClient {
  id: string;
  name: string;
  short_name: string | null;
  notes: string | null;
}

interface SubsidiaryClient {
  id: string;
  name: string;
  short_name: string | null;
  status: string | null;
}

interface Subsidiary {
  client: SubsidiaryClient;
  property_count: number;
  parcel_count: number;
  ytd_savings?: number;
  open_invoice_total?: number;
}

interface RollupTotals {
  subsidiary_count: number;
  property_count: number;
  parcel_count: number;
  ytd_savings?: number;
  open_invoice_total?: number;
}

interface Rollup {
  parent: ParentClient;
  subsidiaries: Subsidiary[];
  totals: RollupTotals;
}

const money = (v: number | null | undefined) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '—';

export function ParentClientRollup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [membersOpen, setMembersOpen] = useState(false);
  // Add clients / Remove member both edit an existing parent's membership
  // (POST/DELETE /api/parent-clients/:id/members) → gate on parent_client.can_edit.
  const canEditParent = useEntityPermission('parent_client', 'can_edit');

  const rollupQuery = useQuery<Rollup>({
    queryKey: ['parent-client-rollup', id],
    queryFn: ({ signal }) => api.get<Rollup>(`/api/parent-client-rollup/${id}`, signal),
    enabled: !!id,
  });

  // Money aggregates are present only when the backend returned them (money-sighted
  // roles). When absent (money-blind administrator), the money columns/tiles hide.
  const showMoney = !!rollupQuery.data && 'ytd_savings' in rollupQuery.data.totals;

  // RH§2 — remove a client from this parent (clears its parent_client_id).
  const removeMutation = useMutation<unknown, Error, string>({
    mutationFn: (clientId) => api.delete(`/api/parent-clients/${id}/members/${clientId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-client-rollup', id] });
      queryClient.invalidateQueries({ queryKey: ['assignable-clients', id] });
      toast({ variant: 'success', message: 'Removed from this parent.' });
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not remove the client.' }),
  });

  const columns = useMemo<ColumnDef<Subsidiary>[]>(() => {
    const cols: ColumnDef<Subsidiary>[] = [
      { id: 'name', header: 'Client', cell: ({ row }) => row.original.client.name },
      { id: 'status', header: 'Status', cell: ({ row }) => (row.original.client.status || '—').replace(/_/g, ' ') },
      { accessorKey: 'property_count', header: 'Properties', cell: ({ getValue }) => (getValue() as number).toLocaleString() },
      { accessorKey: 'parcel_count', header: 'Parcels', cell: ({ getValue }) => (getValue() as number).toLocaleString() },
    ];
    if (showMoney) {
      cols.push(
        { accessorKey: 'ytd_savings', header: 'YTD savings', cell: ({ getValue }) => money(getValue() as number | undefined) },
        { accessorKey: 'open_invoice_total', header: 'Open invoices', cell: ({ getValue }) => money(getValue() as number | undefined) },
      );
    }
    if (canEditParent) {
      cols.push({
        id: 'remove',
        header: '',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); removeMutation.mutate(row.original.client.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-status-red)', font: 'inherit' }}
            title="Remove this client from the parent"
          >Remove</button>
        ),
      });
    }
    return cols;
  }, [showMoney, removeMutation, canEditParent]);

  if (rollupQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading roll-up…</p>;
  }

  if (rollupQuery.error) {
    const notFound = rollupQuery.error instanceof ApiClientError && rollupQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Parent client not found' : 'Failed to load roll-up'}</h2>
        <p style={{ margin: 0 }}>{(rollupQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/parent-clients')}>
            Back to Parent Clients
          </Button>
        </p>
      </div>
    );
  }

  if (!rollupQuery.data) return null;

  const { parent, subsidiaries, totals } = rollupQuery.data;

  return (
    <>
      <EntityHeader
        prefix="Parent client"
        title={parent.name}
        subtitle={parent.short_name && parent.short_name !== parent.name ? parent.short_name : undefined}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/parent-clients')}>
              Back
            </Button>
            {/* RH§2 — add clients to this parent (searchable multi-select). */}
            {canEditParent && (
              <Button variant="primary" size="sm" onClick={() => setMembersOpen(true)}>
                Add clients
              </Button>
            )}
          </>
        }
        kpis={
          <>
            <StatCard label="Subsidiaries" value={String(totals.subsidiary_count)} caption="clients under this parent" accent="hunter" />
            <StatCard label="Properties" value={totals.property_count.toLocaleString()} caption="across the group" accent="info" />
            <StatCard label="Parcels" value={totals.parcel_count.toLocaleString()} caption="across the group" accent="info" />
            {showMoney && (
              <>
                <StatCard label="YTD savings" value={money(totals.ytd_savings)} caption="group tax savings booked" accent="green" />
                <StatCard label="Open invoices" value={money(totals.open_invoice_total)} caption="unpaid across the group" accent="amber" />
              </>
            )}
          </>
        }
      />

      {parent.notes && (
        <div style={{
          marginBottom: 'var(--sp-5)', padding: 'var(--sp-4)', background: 'var(--color-card)',
          border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)',
        }}>{parent.notes}</div>
      )}

      <h2 style={{ margin: '0 0 var(--sp-4)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
        Subsidiaries
      </h2>

      {subsidiaries.length === 0 ? (
        <div style={{
          padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)',
        }}>
          No subsidiaries under this parent yet. Use “Add clients” to pull clients into this parent.
        </div>
      ) : (
        <DataTable
          data={subsidiaries}
          columns={columns}
          onRowClick={(s) => navigate(`/clients/${s.client.id}`)}
        />
      )}

      {id && <ManageParentMembersModal parentId={id} parentName={parent.name} open={membersOpen} onClose={() => setMembersOpen(false)} />}
    </>
  );
}
